import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import RecordOrganizer from "@/components/RecordOrganizer";
import { loadingUnitLabel } from "@/lib/loadingUnits";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, FileText } from "@phosphor-icons/react";
import { useI18n } from "@/lib/i18n";

export default function Plans() {
  const { t } = useI18n();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const nav = useNavigate();

  const load = async () => {
    setLoading(true); setLoadError("");
    try {
      const r = await api.get("/plans");
      setPlans(r.data || []);
    } catch (e) {
      setLoadError(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Bu plan silinsin mi?")) return;
    try { await api.delete(`/plans/${id}`); toast.success(t("final.delete_success")); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title={t("plans.title")} subtitle={t("plans.subtitle")}>
        <Link data-testid="packing-list-designer-cta" to="/packing-list/designer"
          className="inline-flex items-center justify-center gap-2 bg-[#0F172A] hover:bg-[#1E293B] text-white px-4 py-2 text-sm font-semibold rounded-sm w-full sm:w-auto">
          <FileText size={16} weight="bold" /> Packing List Şablonu
        </Link>
        <Link data-testid="new-plan-cta" to="/plans/new"
          className="inline-flex items-center justify-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-4 py-2 text-sm font-semibold rounded-sm w-full sm:w-auto">
          <Plus size={16} weight="bold" /> {t("plans.new_plan")}
        </Link>
      </PageHeader>
      <div className="p-4 sm:p-8">
        {loading && <div className="bg-white border border-slate-200 p-10 text-center text-sm text-slate-500">Planlar yükleniyor…</div>}
        {!loading && loadError && <div className="bg-white border border-red-200 p-6 text-center"><div className="font-bold text-red-700">Planlar alınamadı</div><div className="text-sm text-slate-500 mt-1">{loadError}</div><button onClick={load} className="mt-4 px-4 py-2 bg-blue-600 text-white text-sm font-semibold">Tekrar Dene</button></div>}
        {!loading && !loadError && <RecordOrganizer recordType="plan" items={plans} setItems={setPlans} numberField="plan_no" dateField="date">
          {({ filteredItems, selected, toggle, toggleAll, allVisibleSelected, categories }) => {
            const categoryMap = Object.fromEntries(categories.map((c) => [c.id, c.name]));
            return <div className="bg-white border border-slate-200 overflow-x-auto rounded-sm">
              <table className="data-table w-full min-w-[900px]">
                <thead><tr>
                  <th className="w-10 text-center"><input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} aria-label="Tüm görünen planları seç" /></th>
                  <th>{t("plans.plan_no")}</th><th>Kategori</th><th>{t("plans.shipment")}</th><th>{t("plans.country")}</th><th>{t("plans.date")}</th>
                  <th className="text-right">Yükleme Birimi</th><th className="text-right">{t("plans.fill")}</th><th>{t("plans.status")}</th><th></th>
                </tr></thead>
                <tbody>
                  {filteredItems.map((p) => (
                    <tr key={p.id} data-testid={`plan-row-${p.plan_no}`} className={selected.has(p.id) ? "bg-blue-50" : ""}>
                      <td className="text-center"><input type="checkbox" checked={selected.has(p.id)} onChange={()=>toggle(p.id)} aria-label={`${p.plan_no} seç`} /></td>
                      <td className="font-mono text-xs"><Link to={`/plans/${p.id}`} className="text-[#2563EB] hover:underline">{p.plan_no}</Link></td>
                      <td><span className={`text-[10px] px-2 py-1 border ${p.category_id ? "bg-slate-50 text-slate-700" : "text-slate-400"}`}>{categoryMap[p.category_id] || "Kategorisiz"}</span></td>
                      <td>{p.shipment_name}</td><td>{p.country}</td><td className="font-mono text-xs">{p.date}</td>
                      <td className="text-right data-cell">{p.pallets?.length || 0} {loadingUnitLabel(p)}</td><td className="text-right data-cell">{((p.fill_ratio || 0) * 100).toFixed(1)}%</td>
                      <td><span className={`text-[10px] uppercase px-2 py-0.5 font-semibold ${p.status === "approved" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}>{p.status === "approved" ? t("plans.approved") : t("plans.waiting")}</span></td>
                      <td className="text-right">
                        <button onClick={() => nav(`/plans/${p.id}/edit`)} className="text-slate-500 hover:text-[#2563EB] mr-3" title={t("plans.edit_title")}><PencilSimple size={16} /></button>
                        <button onClick={() => remove(p.id)} className="text-slate-400 hover:text-red-600" title="Sil"><Trash size={16} /></button>
                      </td>
                    </tr>
                  ))}
                  {filteredItems.length === 0 && <tr><td colSpan={10} className="text-center py-12 text-slate-400">Henüz plan yok veya filtreye uygun kayıt bulunamadı.</td></tr>}
                </tbody>
              </table>
            </div>;
          }}
        </RecordOrganizer>}
      </div>
    </div>
  );
}
